import reduce from 'lodash/reduce';
import get from 'lodash/get';
import find from 'lodash/find';
import keyBy from 'lodash/keyBy';

import { dollarStringToNumber, maybeNullDollarString, numberToDollarString } from 'helpers/money';
import { displayPrice } from 'helpers/servings';
import {
  NOT_ENROLLED_BETA_BASKET_STATUS,
  ENROLLED_BETA_BASKET_STATUS,
  WAITLISTED_BETA_BASKET_STATUS,
  PROCESSED_BETA_BASKET_STATUS,
  SHIPPED_BETA_BASKET_STATUS,
  DELIVERED_BETA_BASKET_STATUS,
} from 'utilities/types';

const transformMeal = meal => {
  const displayPriceCents = displayPrice({
    price: meal.basePriceCents,
    displayUnit: meal.displayUnit,
  });

  return {
    ...meal,
    basePriceFormatted: numberToDollarString(meal.basePriceCents / 100),
    basePriceDollars: meal.basePriceCents / 100,
    priceFormatted: meal.price,
    displayPriceCents,
    displayPrice: numberToDollarString(displayPriceCents / 100),
    priceDollars: dollarStringToNumber(meal.price),
    prepMinutesFormatted: meal.prepMinutes + ' min',
    daysCookByFormatted: meal.daysCookBy + (meal.daysCookBy > 1 ? ' days' : ' day'),
    instructions: meal.instructions.map(transformInstruction),
    showCookingGuidelines: !['Snappy Snack', 'Smoothie'].includes(meal.mealAddonName),
  };
};

const transformInstruction = instruction => ({
  ...instruction,
  descriptionMarkdown: instruction.descriptionMarkdown.replace(/\r?\n|\r| \r?\n/g, ' '),
});

export const transformBasketResponse = response => {
  const basket = response.weeklyBasket;

  return {
    ...response,
    weeklyBasket: {
      ...basket,
      recurringDiscountFormatted: numberToDollarString(basket.recurringDiscountCents / 100),
      subtotalFormatted: maybeNullDollarString(basket.subtotal),
      subtotalDollars: dollarStringToNumber(maybeNullDollarString(basket.subtotal)),
      shippingFormatted: maybeNullDollarString(basket.shipping),
      shippingDollars: dollarStringToNumber(maybeNullDollarString(basket.shipping)),
      salesTaxFormatted: maybeNullDollarString(basket.salesTax),
      meals: basket.meals.map(transformMeal),
      totalFormatted: maybeNullDollarString(basket.total),
      totalDollars: dollarStringToNumber(maybeNullDollarString(basket.total)),
      creditFormatted: maybeNullDollarString(basket.credit),
      creditDollars: dollarStringToNumber(maybeNullDollarString(basket.credit)),
    },
  };
};

const transformMealOption = (mealOption, meal) => ({
  ...mealOption,
  displayPriceModifierCents: displayPrice({
    price: mealOption.priceModifierCents,
    displayUnit: meal.displayUnit,
  }),
});

const transformMealOptionGroup = (mealOptionGroup, meal) => ({
  ...mealOptionGroup,
  mealOptions: mealOptionGroup.mealOptions.map(option => transformMealOption(option, meal)),
});

export const transformMenuResponse = response => ({
  menu: {
    ...response,
    meals: response.meals.map(transformMeal),
    mealOptionGroups: response.mealOptionGroups.map(group =>
      transformMealOptionGroup(group, response.meals.find(meal => meal.id === group.mealId))
    ),
  },
});

export const transformMealsResponse = response => ({
  meals: response.meals.map(meal => transformMeal(meal)),
});

export const transformMealResponse = response => ({
  meal: transformMeal(response),
});

const transformBetaBasketStatus = status => {
  switch (status) {
    case 'opted_in':
      return ENROLLED_BETA_BASKET_STATUS;
    case 'opted_out':
      return NOT_ENROLLED_BETA_BASKET_STATUS;
    case 'pending':
      return NOT_ENROLLED_BETA_BASKET_STATUS;
    case 'waitlisted':
      return WAITLISTED_BETA_BASKET_STATUS;
    case 'processed':
      return PROCESSED_BETA_BASKET_STATUS;
    case 'shipped':
      return SHIPPED_BETA_BASKET_STATUS;
    case 'delivered':
      return DELIVERED_BETA_BASKET_STATUS;
  }
};

export const transformBetaBasketResponse = betaBasket => ({
  ...betaBasket,
  status: transformBetaBasketStatus(betaBasket.status),
  meals: betaBasket.meals.map(transformMeal),
});

export const transformBetaBasketsResponse = response => {
  return {
    betaBaskets: response.betaBaskets.map(betaBasket => ({
      ...betaBasket,
      status: transformBetaBasketStatus(betaBasket.status),
      meals: betaBasket.meals.map(transformMeal),
    })),
  };
};

export const transformReceiptResponse = response => {
  const creditAndDiscountDollars = (response.creditCents + response.discountCents) / 100;
  return {
    ...response,
    creditDollars: Number(response.creditCents) / 100,
    creditAndDiscountDollars,
    creditAndDiscountFormatted: numberToDollarString(creditAndDiscountDollars),
    discountDollars: Number(response.discountCents) / 100,
    recurringDiscountFormatted: numberToDollarString(response.recurringDiscountCents / 100),
    subtotalFormatted: numberToDollarString(response.subtotal),
    subtotalDollars: response.subtotal,
    shippingFormatted: numberToDollarString(response.shippingCost),
    shippingDollars: response.shippingCost,
    salesTaxFormatted: numberToDollarString(response.salesTax),
    totalDollars: response.total,
    totalFormatted: numberToDollarString(response.total),
  };
};

export const transformShoppingListMealResponse = response => ({
  shoppingListMeal: {
    ...response.shoppingListMeal,
    ingredients: response.shoppingListMeal.ingredients.map(ingredient => ({
      ...ingredient,
      quantity: Number(ingredient.quantity),
    })),
  },
});

export const transformFavoritesResponse = response => ({
  favorites: response.favorites.reduce(
    (obj, favorite) => ({
      ...obj,
      [favorite.mealConcept]: favorite.id,
    }),
    {}
  ),
});

export const transformFavoriteResponse = response => ({
  favorite: {
    [response.favorite.mealConcept]: response.favorite.id,
  },
});

export const transformMealOptionSelections = (mealOptionSelections, meals) => {
  return reduce(
    mealOptionSelections,
    (transformedMealOptions, mealOptions, mealId) => {
      const meal = find(meals, { id: mealId });
      transformedMealOptions[mealId] = meal
        ? mealOptions.map(option => transformMealOption(option, meal))
        : [];
      return transformedMealOptions;
    },
    {}
  );
};

export const transformPastOrdersResponse = response => {
  return {
    ...response,
    weeklyBaskets: response.weeklyBaskets.map(basket => ({
      id: basket.id,
      total: basket.total,
      menuId: basket.menuId,
      chargedDate: basket.chargedDate,
      deliveryDate: basket.deliveryDate,
      status: basket.skipped ? 'Cancelled' : basket.status,
    })),
  };
};

export const transformPastOrderResponse = response => ({
  weeklyBasket: {
    ...response.weeklyBasket,
    meals: response.weeklyBasket.meals.map(transformMeal),
    mealOptionSelections: transformMealOptionSelections(
      response.weeklyBasket.mealOptionSelections,
      response.weeklyBasket.meals
    ),
  },
});

export const transformProduct = product => {
  const frontImgs = find(product.images, { featured: true }).sizes;
  return {
    ...product,
    name: product.description,
    smImg: get(find(frontImgs, { size: 'small' }), 'url', ''),
    mdImg: get(find(frontImgs, { size: 'large' }), 'url', ''),
    lgImg: get(find(frontImgs, { size: 'large' }), 'url', ''),
    xlImg: get(find(frontImgs, { size: 'xlarge' }), 'url', ''),
    unitSize: product.items[0].size,
    price: numberToDollarString(product.priceCents / 100),
  };
};

export const transformProductsResponse = response => ({
  products: keyBy(response.products.map(transformProduct), 'upc'),
});

export const transformPantryCartResponse = response => ({
  pantryCart: {
    ...response.pantryCart,
    lineItems: keyBy(response.pantryCart.lineItems, 'krogerId'),
  },
});

export const transformInventoryResponse = response => ({
  products: response.products.reduce(
    (obj, product) => ({
      ...obj,
      [product.id]: {
        ...product,
        quantityAvailable: product.quantity,
      },
    }),
    {}
  ),
});
